import React from 'react';
import PropTypes from 'prop-types';
import Column from 'components/ui/show/Column';
import DateUTCField from 'components/field/DateFields/DateUTCField';
import FieldTitle from 'components/aor/FieldTitle';
import filterMatchTypes from 'components/list/Filters/filterMatchTypes';
import FlatButton from 'material-ui/FlatButton';
import InnerColumn from 'components/ui/show/InnerColumn';
import Loader from 'components/loading/Loader';
import SingleDataContainer from 'containers/Data/SingleDataContainer';
import HeaderColumn from 'components/ui/table/HeaderColumn';
import Table from 'components/ui/table/Table';
import TableRow from 'components/ui/table/TableRow';
import TableBodyCell from 'components/table/TableBodyCell';
import apiClient from 'clients/apiClient';
import config from 'helpers/config';
import styled from 'styled-components';
import theme from 'theme/theme';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import { Link } from 'react-router-dom';
import { ListIcon } from 'assets/materialIcons/materialIcons';
import { fetchSubcategory } from 'store/actions/subcategories/fetchSubcategories';

const StyledColumn = styled(HeaderColumn)`
    font-weight: 500;
    background-color: ${ theme.palette.ligthGray };
`;

const StyledLink = styled.div`
    a {
        display: flex;
        height: 48px;
        width: 100%;
        align-items: center;
        justify-content: ${ (props) => {
            return props.align;
        } };
        text-decoration: none;
        color: ${ theme.table.body.row.color };

        &:hover {
            text-decoration: none;
            color: ${ theme.table.body.row.color };
            cursor: pointer;
        }
    }
`;

const StyledBlock = (props) => {
    const { align, id, children } = props;

    return (
        <StyledLink align={ align }>
            <Link to={ `/products/${ id }/show` }>
                { children }
            </Link>
        </StyledLink>
    );
};

StyledBlock.defaultProps = {
    align: 'left',
};

StyledBlock.propTypes = {
    align: PropTypes.string,
    children: PropTypes.node,
    id: PropTypes.string.isRequired,
};

const Button = (props) => {
    const {
        history,
        id,
        label,
    } = props;

    const handleChange = () => {
        const filter = {
            provider_id: {
                matchTypes: filterMatchTypes.equals,
                value: id,
            },
        };

        const encodedFilterString = encodeURIComponent(JSON.stringify(filter));
        const url = `/products?filter=${ encodedFilterString }&filterCategory=advanced`;

        return history.push(url);
    };

    return <FlatButton
        icon={ <ListIcon color={ theme.palette.primary1Color } /> }
        hoverColor='rgba(0, 0, 0, 0.1)'
        label={ label }
        labelPosition='after'
        onTouchTap={ handleChange }
        primary={ true }
        style={ {
            borderRadius: '0 0 0 5px',
        } }
    />;
};

Button.propTypes = {
    history: PropTypes.object.isRequired,
    id: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
};

class AccountProductsList extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            results: [],
            total: 0,
        };
    }

    componentDidMount() {
        const { id } = this.props;

        const params = {
            includes: [
                'category',
            ],
            provider_id: id,
            sort_by: 'updated_at',
            sort_direction: 'desc',
        };

        const url = `${ config.getApiBaseUrl() }/products`;

        apiClient.read(url, params).then((response) => {
            this.handleData(response);
        });
    }

    shouldComponentUpdate(nextProps, nextState) {
        const { results, total } = nextState;

        if (results || total) {
            return true;
        }
        return false;
    }

    handleData = (response) => {
        const { maximumResults } = this.props;
        let convertedResults = {};

        // Converts the Array of results to an Object based on the id.
        utils.each(response.results, (value, index) => {
            if (index >= maximumResults) {
                return;
            }

            convertedResults[value.id] = value;
        });

        this.setState({
            loading: false,
            results: convertedResults,
            total: response.total,
        });
    }

    render() {
        const { id, history, maximumResults, translate } = this.props;
        const { initialLoading, results, total } = this.state;

        if (initialLoading) {
            return (
                <div>
                    <Loader />
                </div>
            );
        }

        if (utils.isEmpty(results)) {
            return (
                <Column
                    title={ translate('resources.accounts.forms.products') }
                >
                    <InnerColumn>
                        <p>No products!</p>
                    </InnerColumn>
                </Column>
            );
        }

        return (
            <Column
                title={ translate('resources.accounts.forms.products') }
                actions={ total > maximumResults ?
                    <Button id={ id } history={ history } label={ translate('resources.accounts.products.actions.list', { total: total }) } /> :
                    null
                }
            >
                <InnerColumn>
                    <Table responsive='scroll'>
                        <TableRow>
                            <StyledColumn width='auto'>
                                <FieldTitle
                                    label='resources.products.fields.title'
                                    source='company_name'
                                    resource='products'
                                />
                            </StyledColumn>

                            <StyledColumn width='15%'>
                                <FieldTitle
                                    label='resources.products.fields.enabled'
                                    source='enabled'
                                    resource='products'
                                />
                            </StyledColumn>

                            <StyledColumn width='15%'>
                                <FieldTitle
                                    label='resources.products.fields.category_id'
                                    source='category_id'
                                    resource='products'
                                />
                            </StyledColumn>

                            <StyledColumn width='15%'>
                                <FieldTitle
                                    label='resources.products.fields.subcategory_id'
                                    source='subcategory_id'
                                    resource='products'
                                />
                            </StyledColumn>

                            <StyledColumn width='15%'>
                                <FieldTitle
                                    label='resources.products.fields.updated_at'
                                    source='updated_at'
                                    resource='products'
                                />
                            </StyledColumn>
                        </TableRow>

                        { utils.map(results, (product, key) => {
                            let activity = translate('resources.products.fields.inactive');

                            if (product.enabled) {
                                activity = translate('resources.products.fields.active');
                            }

                            return (
                                <TableRow key={ key }>
                                    <TableBodyCell
                                        field={
                                            <StyledBlock id={ product.id }>
                                                { product.title }
                                            </StyledBlock>
                                        }
                                        record={ product }
                                        source='title'
                                    />
                                    <TableBodyCell
                                        field={
                                            <StyledBlock id={ product.id }>
                                                { activity }
                                            </StyledBlock>
                                        }
                                        record={ product }
                                    />
                                    <TableBodyCell
                                        field={
                                            <StyledBlock id={ product.id }>
                                                { product.relationships.category.data.name }
                                            </StyledBlock>
                                        }
                                        record={ product }
                                        source='category.name'
                                    />

                                    <TableBodyCell
                                        field={
                                            <StyledBlock id={ product.id }>
                                                <SingleDataContainer
                                                    action={ fetchSubcategory }
                                                    id={ product.subcategory_id }
                                                    resource='subcategories'
                                                >
                                                    { (renderProps) => {
                                                        const { data } = renderProps;

                                                        if (!data.name) {
                                                            return null;
                                                        }

                                                        return (
                                                            <span>{ data.name }</span>
                                                        );
                                                    }}
                                                </SingleDataContainer>
                                            </StyledBlock>
                                        }
                                        record={ product }
                                        resource='subcategories'
                                    />
                                    <TableBodyCell
                                        field={
                                            <StyledBlock id={ product.id }>
                                                <DateUTCField
                                                    label='resources.products.fields.created_at'
                                                    record={ product }
                                                    source='updated_at'
                                                />
                                            </StyledBlock>
                                        }
                                        record={ product }
                                        source='updated_at'
                                    />
                                </TableRow>
                            );
                        }) }
                    </Table>
                </InnerColumn>
            </Column>
        );
    }
}

AccountProductsList.defaultProps = {
    maximumResults: 5,
};

AccountProductsList.propTypes = {
    history: PropTypes.object.isRequired,
    id: PropTypes.number.isRequired,
    maximumResults: PropTypes.number,
    translate: PropTypes.func.isRequired,
};

export default translate(AccountProductsList);



// WEBPACK FOOTER //
// ./src/pages/accounts/show/AccountProductsList.js